// src/contexts/PlayerStatsContext.tsx - FULLY UPDATED
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { firestore, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { PlayerStats } from '../types';
import { useAuth } from './AuthContext';
import { ensureCharacter } from '../services/PlayerService';
import { checkRankUpdate } from '../utils/rankUtils';
import { updateLastSeenIfNeeded } from '../services/LastSeenService';
import { getCurrentServer, getSelectedServer, getServerSpecificId } from '../utils/serverUtils';
import { GlobalUserService } from '../services/GlobalUserService';

interface PlayerStatsContextType {
    playerStats: PlayerStats | null;
    pollid: number;
    isVip: boolean;
    loading: boolean;
    refreshStats: () => Promise<void>;
}

const PlayerStatsContext = createContext<PlayerStatsContextType>({
    playerStats: null,
    pollid: 0,
    isVip: false,
    loading: true,
    refreshStats: async () => {},
});

export const usePlayerStats = () => useContext(PlayerStatsContext);

export const PlayerStatsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null);
    const [pollid, setPollid] = useState<number>(0);
    const [isVip, setIsVip] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [currentServer, setCurrentServer] = useState<string>(getCurrentServer());
    // The world actually chosen, which is not the same thing: `currentServer` falls back to
    // beta so consumers never see null, and that fallback made "no world yet" and "beta"
    // indistinguishable — so entering beta from the selection page changed no state and the
    // listener never started. This is the value the setup effect keys on.
    const [selectedWorld, setSelectedWorld] = useState<string | null>(getSelectedServer());

    const lastLevelRef = useRef<number | null>(null);
    const isUpdatingRankRef = useRef<boolean>(false);
    const unsubscribeRef = useRef<(() => void) | null>(null);
    const globalUnsubscribeRef = useRef<(() => void) | null>(null);

    // Manual refresh function - simplified to avoid loops
    const refreshStats = useCallback(async () => {
        if (!currentUser) return;

        try {
            // Use server-specific document ID
            const statsDocId = getServerSpecificId(currentUser.uid, currentServer);
            const statsRef = doc(firestore, 'playerStats', statsDocId);
            const statsDoc = await getDoc(statsRef);

            // ADD - Refresh global data too
            const globalData = await GlobalUserService.getGlobalUserData(currentUser.uid);
            setPollid(globalData.pollid);
            setIsVip(globalData.isVip);

            if (statsDoc.exists()) {
                const stats = statsDoc.data() as PlayerStats;

                // Don't set state here if we have an active listener
                if (!unsubscribeRef.current) {
                    setPlayerStats(stats);
                }
            }
        } catch (err) {
            console.error('Error refreshing stats:', err);
        }
    }, [currentUser, currentServer]);

    // Update lastSeen when user is active
    useEffect(() => {
        if (!currentUser) return;

        // Use BASE userId - LastSeenService adds server suffix itself
        const activityInterval = setInterval(() => {
            if (playerStats?.lastSeen !== undefined) {
                updateLastSeenIfNeeded(currentUser.uid, playerStats.lastSeen);
            } else {
                updateLastSeenIfNeeded(currentUser.uid);
            }
        }, 5 * 60 * 1000);

        if (playerStats) {
            updateLastSeenIfNeeded(currentUser.uid, playerStats.lastSeen);
        }

        return () => clearInterval(activityInterval);
    }, [currentUser, playerStats]);

    // ADD - Set up listener for global user data
    useEffect(() => {
        if (!currentUser) {
            setPollid(0);
            setIsVip(false);
            return;
        }

        // Listen to global user document for pollid/VIP changes
        const userRef = doc(firestore, 'users', currentUser.uid);
        const unsubscribe = onSnapshot(
            userRef,
            (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const userData = docSnapshot.data();
                    setPollid(userData.pollid || 0);
                    setIsVip(userData.isVip || false);
                }
            },
            (error) => {
                console.error('Global user listener error:', error);
            }
        );

        globalUnsubscribeRef.current = unsubscribe;

        return () => {
            if (globalUnsubscribeRef.current) {
                globalUnsubscribeRef.current();
                globalUnsubscribeRef.current = null;
            }
        };
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) {
            setPlayerStats(null);
            setLoading(false);
            return;
        }

        // No world chosen yet: the player is on the selection page. Creating stats here
        // would plant a character in whatever world the fallback names, which is exactly
        // what world selection exists to stop.
        if (!selectedWorld) {
            setPlayerStats(null);
            setLoading(false);
            return;
        }

        // From here on the chosen world is the only source of truth for which documents
        // this listener reads; re-reading localStorage mid-effect is what the old
        // "fresh server" dance did, and it raced with the state it was trying to correct.
        const freshServer = selectedWorld;

        const setupListener = async () => {

            try {
                // Get server-specific document ID using FRESH server value
                const statsDocId = getServerSpecificId(currentUser.uid, freshServer);
                console.log('Stats Doc ID:', statsDocId);

                // ADD - Load global data immediately
                const globalData = await GlobalUserService.getGlobalUserData(currentUser.uid);
                setPollid(globalData.pollid);
                setIsVip(globalData.isVip);

                // One read if the character exists; the server creates it if this world has
                // none for the account. The username it needs comes from the account record
                // on the server, so the extra `users` read that used to sit here is gone.
                await ensureCharacter(freshServer, statsDocId);

                // Set up the real-time listener with server-specific ID
                const statsRef = doc(firestore, 'playerStats', statsDocId);

                const unsubscribe = onSnapshot(
                    statsRef,
                    async (docSnapshot) => {
                        if (docSnapshot.exists()) {
                            const stats = docSnapshot.data() as PlayerStats;

                            setPlayerStats(stats);

                            // Only check rank if level changed and we're not already updating
                            const currentLevel = stats.level || 1;
                            if (!isUpdatingRankRef.current &&
                                (lastLevelRef.current === null || lastLevelRef.current !== currentLevel)) {

                                lastLevelRef.current = currentLevel;

                                // Rank drives the hourly wage, so the server derives and
                                // writes it. The browser only notices that its level moved
                                // and asks for a re-check; it cannot propose a rank.
                                const newRank = checkRankUpdate(stats);
                                if (newRank !== null && newRank !== stats.rank) {
                                    isUpdatingRankRef.current = true;
                                    try {
                                        const sync = httpsCallable<{ server: string }, { rank: string | null; changed: boolean }>(functions, 'syncRank');
                                        await sync({ server: getCurrentServer() });
                                    } catch (error) {
                                        console.error('Error syncing rank:', error);
                                    } finally {
                                        isUpdatingRankRef.current = false;
                                    }
                                }
                            }
                        } else {
                            setPlayerStats(null);
                        }
                        setLoading(false);
                    },
                    (error) => {
                        console.error('PlayerStats listener error:', error);
                        setLoading(false);
                    }
                );

                unsubscribeRef.current = unsubscribe;
            } catch (err) {
                console.error('Failed to setup stats listener:', err);
                setLoading(false);
            }
        };

        setupListener();

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [currentUser, selectedWorld]); // Re-run when the chosen world changes

    // Listen for server changes from localStorage
    useEffect(() => {
        const applyWorld = (world: string | null) => {
            setSelectedWorld(world);
            setCurrentServer(world ?? getCurrentServer());
            setLoading(!!world);
        };

        const handleStorageChange = () => {
            const chosen = getSelectedServer();
            if (chosen !== selectedWorld) applyWorld(chosen);
        };

        // Check immediately on mount: another tab may have switched worlds.
        const chosenOnMount = getSelectedServer();
        if (chosenOnMount !== selectedWorld) applyWorld(chosenOnMount);

        window.addEventListener('storage', handleStorageChange);

        // CRITICAL: Also listen to custom event for same-tab changes
        const handleCustomServerChange = (e: CustomEvent) => {
            const chosen = (e.detail?.server as string | undefined) ?? getSelectedServer();
            if (chosen !== selectedWorld) applyWorld(chosen ?? null);
        };

        window.addEventListener('serverChanged', handleCustomServerChange as EventListener);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('serverChanged', handleCustomServerChange as EventListener);
        };
    }, [selectedWorld]);

    return (
        <PlayerStatsContext.Provider value={{
            playerStats,
            pollid,
            isVip,
            loading,
            refreshStats,
        }}>
            {children}
        </PlayerStatsContext.Provider>
    );
};